import React from 'react';
import MetricChart from '../../components/MetricChart';
import { formatTime } from '../../utils/formatTime';
import { Panel, Badge } from '../../components/design';
import Icon from '../../components/Icon';

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return null;
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function OverviewTab({ server, cpuSeries, cpuValue, memSeries, memValue, diskSeries, diskValue }) {
  const metricColor = (val) => {
    if (!val) return 'var(--text-3)';
    const n = parseFloat(val);
    if (n > 90) return 'var(--danger)';
    if (n > 70) return 'var(--warning)';
    return 'var(--success)';
  };

  const enrichment = server.enrichment || {};
  const prom = enrichment.prometheus || null;
  const wazuh = enrichment.wazuh || null;
  const install = enrichment.install || null;
  const counts = enrichment.counts || {};
  const uptime = formatUptime(prom?.uptime_seconds);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
        <Panel title="CPU Usage" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(cpuValue) }}>{cpuValue ? `${cpuValue}%` : 'N/A'}</span>}>
          <MetricChart data={cpuSeries} height={60} color="#6366f1" />
        </Panel>
        <Panel title="Memory Usage" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(memValue) }}>{memValue ? `${memValue}%` : 'N/A'}</span>}>
          <MetricChart data={memSeries} height={60} color="#8b5cf6" />
        </Panel>
        <Panel title="Disk Usage" actions={<span style={{ fontWeight: 700, fontSize: 22, color: metricColor(diskValue) }}>{diskValue ? `${diskValue}%` : 'N/A'}</span>}>
          <MetricChart data={diskSeries} height={60} color="#06b6d4" />
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
        <Panel title="Connection Info">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div className="flex between"><span className="muted">IP Address</span><span className="mono" style={{ color: 'var(--text-1)' }}>{server.ip_address}</span></div>
            <div className="flex between"><span className="muted">SSH User</span><span style={{ color: 'var(--text-1)' }}>{server.ssh_user}</span></div>
            <div className="flex between"><span className="muted">SSH Port</span><span style={{ color: 'var(--text-1)' }}>{server.ssh_port}</span></div>
            <div className="flex between"><span className="muted">OS Type</span><span style={{ color: 'var(--text-1)' }}>{server.os_type || wazuh?.os_type_norm || '—'}</span></div>
            {server.os_version && <div className="flex between"><span className="muted">OS Version</span><span style={{ color: 'var(--text-1)' }}>{server.os_version}{wazuh?.os_codename ? ` (${wazuh.os_codename})` : ''}</span></div>}
            {server.os_arch && <div className="flex between"><span className="muted">Architecture</span><span style={{ color: 'var(--text-1)' }}>{server.os_arch}</span></div>}
            {prom?.release && <div className="flex between"><span className="muted">Kernel</span><span className="mono" style={{ color: 'var(--text-1)' }}>{prom.release}</span></div>}
            {uptime && <div className="flex between"><span className="muted">Uptime</span><span style={{ color: 'var(--text-1)' }}>{uptime}</span></div>}
            {server.hostname && <div className="flex between"><span className="muted">Hostname</span><span style={{ color: 'var(--text-1)' }}>{server.hostname}</span></div>}
          </div>
          {(server.tags || server.environment || server.team) && (
            <div className="flex items-center gap-2 flex-wrap" style={{ paddingTop: 12, marginTop: 12, borderTop: '1px solid var(--glass-border)', fontSize: 12 }}>
              {server.environment && <Badge tone="blue" dot={false}>env: {server.environment}</Badge>}
              {server.team && <Badge tone="pink" dot={false}>team: {server.team}</Badge>}
              {(Array.isArray(server.tags) ? server.tags : []).map(tag => (
                <Badge key={tag} tone="gray" dot={false}>{tag}</Badge>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Monitoring Status">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(() => {
              const exporters = server.exporters || [];
              const nodeExporter = exporters.find(e => e.name === 'node_exporter');
              const wazuhAgent = exporters.find(e => e.name === 'wazuh_agent');
              const cockpit = exporters.find(e => e.name === 'cockpit');
              return (
                <>
                  <div>
                    <div className="flex between" style={{ fontSize: 13, marginBottom: 4 }}>
                      <span className="muted">Prometheus (Node Exporter)</span>
                      <Badge tone={nodeExporter?.enabled ? 'green' : 'gray'}>{nodeExporter?.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </div>
                    {nodeExporter?.enabled && <div className="muted" style={{ fontSize: 11 }}>Port: {nodeExporter.port || 9100} &bull; Target: {server.ip_address}:{nodeExporter.port || 9100}{nodeExporter.last_checked_at ? ` &bull; Checked: ${formatTime(nodeExporter.last_checked_at)}` : ''}</div>}
                  </div>
                  <div>
                    <div className="flex between" style={{ fontSize: 13, marginBottom: 4 }}>
                      <span className="muted">Wazuh Agent</span>
                      <Badge tone={wazuhAgent?.enabled ? 'green' : 'gray'}>{wazuhAgent?.enabled ? 'Enabled' : 'Disabled'}</Badge>
                    </div>
                    {wazuhAgent?.enabled && <div className="muted" style={{ fontSize: 11 }}>Port: {wazuhAgent.port || 1514} &bull; Version: {wazuh?.agent_version || server.agent_version || 'Unknown'}</div>}
                  </div>
                  {cockpit?.enabled && (
                    <div>
                      <div className="flex between" style={{ fontSize: 13, marginBottom: 4 }}>
                        <span className="muted">Cockpit</span>
                        <div className="flex items-center gap-2">
                          <Badge tone={cockpit.active ? 'green' : 'amber'}>{cockpit.active ? 'Active' : 'Enabled'}</Badge>
                          {cockpit.active && (
                            <a href={`https://${server.ip_address?.split('/')[0]}:${cockpit.port || 9090}`} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--accent-1)' }}>
                              <Icon name="arrow-up-right" size={12} />
                              Open
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>Port: {cockpit.port || 9090} &bull; Web Server Management</div>
                    </div>
                  )}
                </>
              );
            })()}
            <div style={{ paddingTop: 12, borderTop: '1px solid var(--glass-border)' }}>
              <div className="flex between" style={{ fontSize: 13 }}>
                <span className="muted">Last Seen</span>
                <span className="muted" style={{ fontSize: 12 }}>{server.last_seen ? formatTime(server.last_seen) : 'Never'}</span>
              </div>
              {server.created_at && (
                <div className="flex between" style={{ fontSize: 13, marginTop: 6 }}>
                  <span className="muted">Created</span>
                  <span className="muted" style={{ fontSize: 12 }}>{formatTime(server.created_at)}</span>
                </div>
              )}
              {server.updated_at && (
                <div className="flex between" style={{ fontSize: 13, marginTop: 6 }}>
                  <span className="muted">Updated</span>
                  <span className="muted" style={{ fontSize: 12 }}>{formatTime(server.updated_at)}</span>
                </div>
              )}
              {(counts.open_incidents > 0 || counts.active_alerts > 0) && (
                <div className="flex gap-2" style={{ marginTop: 12, fontSize: 12 }}>
                  {counts.open_incidents > 0 && <Badge tone="red" dot={false}>{counts.open_incidents} open incidents</Badge>}
                  {counts.active_alerts > 0 && <Badge tone="amber" dot={false}>{counts.active_alerts} firing alerts</Badge>}
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>

      {wazuh && (
        <Panel title="Wazuh Agent">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, fontSize: 13 }}>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Agent</div>
              <div style={{ fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wazuh.agent_id || '—'} · {wazuh.agent_name || '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Status</div>
              <div style={{ fontWeight: 500, color: wazuh.status === 'active' ? 'var(--success)' : 'var(--text-1)' }}>{wazuh.status || '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Version</div>
              <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{wazuh.agent_version || '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Last Keep-Alive</div>
              <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{wazuh.last_keepalive ? formatTime(wazuh.last_keepalive) : '—'}</div>
            </div>
            {wazuh.os_uname && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className="muted" style={{ fontSize: 12 }}>OS</div>
                <div className="mono" style={{ color: 'var(--text-1)', fontSize: 12, marginTop: 2, wordBreak: 'break-all' }}>{wazuh.os_uname}</div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {install?.progress && (
        <Panel title="Agent Install Progress">
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(install.progress).map(([agent, p]) => {
              const status = p?.status || 'unknown';
              const tone = status === 'done' ? 'green'
                : status === 'failed' ? 'red'
                : status === 'conflict' ? 'amber'
                : 'blue';
              return (
                <Badge key={agent} tone={tone} dot={false}>
                  {agent}: {status}{p?.attempt ? ` (attempt ${p.attempt})` : ''}
                </Badge>
              );
            })}
            {install.token_active && (
              <Badge tone="blue" dot={false}>install token active</Badge>
            )}
          </div>
        </Panel>
      )}
    </div>
  );
}
